import React, { useState } from 'react'
import moment from 'moment'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Typography from '@material-ui/core/Typography'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp'

// Sort page visits chronologically (defensive - assume they may not be ordered).
function sortVisits(pageVisits) {
    return [...pageVisits]
        .filter((visit) => visit && visit.timestamp)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
}

// Format a duration in milliseconds as a compact human string e.g. "1m 30s".
function formatDuration(ms) {
    if (ms == null || ms < 0 || !isFinite(ms)) return null
    const totalSeconds = Math.round(ms / 1000)
    if (totalSeconds < 1) return '<1s'
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const parts = []
    if (hours) parts.push(`${hours}h`)
    if (minutes) parts.push(`${minutes}m`)
    if (seconds || parts.length === 0) parts.push(`${seconds}s`)
    return parts.join(' ')
}

export default function PageJourney(props) {
    const lead = props.lead || {}
    const pageVisits = Array.isArray(lead.pageVisits) ? lead.pageVisits : []
    const [showJourney, setShowJourney] = useState(false)

    const visits = sortVisits(pageVisits)
    const hasVisits = visits.length > 0

    // Fallback: no page visits but a legacy lastInternalPage exists (or neither).
    if (!hasVisits) {
        return (
            <Grid item xs={12} md={6}>
                <FormGroup>
                    <FormLabel>Last Internal Page</FormLabel>
                    <Box>{lead.lastInternalPage ? lead.lastInternalPage : '-'}</Box>
                </FormGroup>
            </Grid>
        )
    }

    const firstPage = visits[0].page || '-'
    const lastPage = visits[visits.length - 1].page || '-'

    // Total time on site = last visit timestamp - first visit timestamp.
    const totalMs = new Date(visits[visits.length - 1].timestamp).getTime()
        - new Date(visits[0].timestamp).getTime()
    const totalDuration = visits.length > 1 ? formatDuration(totalMs) : null

    const journeyLabelStyle = {
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        color: '#5e72e4',
        fontWeight: 600,
        fontSize: '0.8125rem',
        userSelect: 'none',
    }

    return (
        <>
            <Grid item xs={12} md={6}>
                <FormGroup>
                    <FormLabel>First Internal Page</FormLabel>
                    <Box>{firstPage}</Box>
                </FormGroup>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormGroup>
                    <FormLabel>Last Internal Page</FormLabel>
                    <Box>{lastPage}</Box>
                </FormGroup>
            </Grid>
            <Grid item xs={12}>
                <Box
                    onClick={() => setShowJourney(!showJourney)}
                    style={journeyLabelStyle}
                >
                    {showJourney ? (
                        <KeyboardArrowUp style={{ fontSize: '1.25rem', marginRight: 4 }} />
                    ) : (
                        <KeyboardArrowDown style={{ fontSize: '1.25rem', marginRight: 4 }} />
                    )}
                    {showJourney ? 'Hide full journey' : `View full journey (${visits.length} ${visits.length === 1 ? 'page' : 'pages'})`}
                    {totalDuration && (
                        <Box
                            component='span'
                            style={{ marginLeft: 8, color: '#8898aa', fontWeight: 400 }}
                        >
                            · {totalDuration} on site
                        </Box>
                    )}
                </Box>
                {showJourney && (
                    <Box style={{ marginTop: 16 }}>
                        {visits.map((visit, index) => {
                            const isLast = index === visits.length - 1
                            const durationMs = isLast
                                ? null
                                : new Date(visits[index + 1].timestamp).getTime()
                                    - new Date(visit.timestamp).getTime()
                            const duration = formatDuration(durationMs)
                            return (
                                <Box
                                    key={index}
                                    style={{ display: 'flex', alignItems: 'stretch' }}
                                >
                                    {/* Timeline rail: dot + connector */}
                                    <Box
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            marginRight: 16,
                                        }}
                                    >
                                        <Box
                                            style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: isLast ? '#2dce89' : '#5e72e4',
                                                marginTop: 4,
                                                flexShrink: 0,
                                            }}
                                        />
                                        {!isLast && (
                                            <Box
                                                style={{
                                                    width: 2,
                                                    flexGrow: 1,
                                                    minHeight: 24,
                                                    backgroundColor: '#e9ecef',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    {/* Visit details */}
                                    <Box style={{ paddingBottom: isLast ? 0 : 20, flexGrow: 1 }}>
                                        <Box
                                            component={Typography}
                                            variant='body2'
                                            style={{ fontWeight: 600, wordBreak: 'break-all' }}
                                        >
                                            {visit.page || '-'}
                                        </Box>
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                marginTop: 2,
                                            }}
                                        >
                                            <Box
                                                component='span'
                                                style={{ color: '#8898aa', fontSize: '0.75rem' }}
                                            >
                                                {moment(visit.timestamp).format('MMM D, YYYY h:mm:ss a')}
                                            </Box>
                                            <Box
                                                component='span'
                                                style={{
                                                    marginLeft: 8,
                                                    padding: '2px 8px',
                                                    borderRadius: 12,
                                                    fontSize: '0.6875rem',
                                                    fontWeight: 600,
                                                    backgroundColor: isLast ? '#eaf7f1' : '#eef0fb',
                                                    color: isLast ? '#2dce89' : '#5e72e4',
                                                }}
                                            >
                                                {isLast ? 'Last page' : (duration ? `${duration} on page` : '—')}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </Grid>
        </>
    )
}
